import { useMemo } from "react";
import { isMatchedToInvoiceOrBill } from "@common/helpers/transactions";
import { PREMIUM_FEATURES } from "@common/flatRateBilling";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { TRANSACTIONS_PAGE_SIZE } from "@components/transactions/useTransactionsQuery";
import { useAllowFeature } from "@hooks/useAllowFeature";
import { useLockList } from "@api/lock.api";
import { getLockDate, isLockedByDateOnly } from "@helpers/lock";
import { toUTCDateOnly } from "@common/helpers/date";
import { useLedgers } from "@api/ledger.api";
import { findPrimaryLedgerId } from "@lib/accountOverrides";
import { useBulkActions } from "./TransactionsBulkActions/useBulkActions";
import { canInvoiceSelectedTransactionsToCustomer } from "./TransactionsBulkActions/buildBillableExpenseInvoicePrefill";
import {
    hasInvoicedTransactionsInSelection as computeHasInvoicedTransactionsInSelection,
    partitionSelectionByInvoiceOrBillLock,
    partitionSelectionByScheduleEntityLock,
    partitionSelectionByScheduleLock,
    hasSuggestedBillMatch,
    hasSuggestedInvoiceMatch,
    shouldShowAcceptSuggestedMatchesButton,
    shouldShowMatchButton as computeShouldShowMatchButton,
    shouldShowRemoveCustomerButton as computeShouldShowRemoveCustomerButton,
    shouldShowSetScheduleButton as computeShouldShowSetScheduleButton,
    shouldShowUnmatchInvoicesButton as computeShouldShowUnmatchInvoicesButton,
} from "./useTransactionsBulkActionConditions.helpers";

/**
 * Computes conditions for which bulk actions should be available
 * Used to control both UI visibility and keyboard shortcut availability
 */
export function useTransactionsBulkActionConditions() {
    const bulkActions = useBulkActions();
    const { hasAccess: areClassesEnabled } = useAllowFeature(
        PREMIUM_FEATURES.CLASSES,
    );

    const selectedArray = bulkActions.selectedArray;
    const uniqueEntityIds = useMemo(
        () => [...new Set(selectedArray.map((t) => t.entity.id))],
        [selectedArray],
    );
    const locks = useLockList(uniqueEntityIds);

    const singleEntityId =
        uniqueEntityIds.length === 1 ? uniqueEntityIds[0] : undefined;

    const { data: ledgers } = useLedgers(singleEntityId);

    const hasAccrualLedger = useMemo(
        () =>
            (ledgers ?? []).some(
                (l) => l.basis === AccountingLedgerBasis.accruals,
            ),
        [ledgers],
    );
    const primaryAccrualLedgerId = useMemo(
        () => findPrimaryLedgerId(ledgers, AccountingLedgerBasis.accruals),
        [ledgers],
    );

    return useMemo(() => {
        const lockDateByEntityId = locks.reduce<Record<number, Date | null>>(
            (acc, lock) => {
                acc[lock.entityId] = getLockDate([lock]);
                return acc;
            },
            {},
        );
        const areAnySelectedTransactionsLocked =
            selectedArray.length > 0 &&
            selectedArray.some((t) =>
                isLockedByDateOnly(
                    lockDateByEntityId[t.entity.id] ?? null,
                    t.dateOnly ?? toUTCDateOnly(t.date),
                ),
            );
        const selectedTransactionsHaveTransferMatch = selectedArray.filter(
            (value) => value.pairedTransferId,
        );

        const shouldShowUnmatchAllButton =
            selectedTransactionsHaveTransferMatch.length > 0 &&
            selectedTransactionsHaveTransferMatch.length ===
                bulkActions.selectedCount;

        const shouldShowMatchButton = computeShouldShowMatchButton({
            selectedArray,
            hasSelectedAll: bulkActions.hasSelectedAll,
        });

        const shouldShowUnmatchInvoicesButton =
            computeShouldShowUnmatchInvoicesButton({
                selectedArray,
                hasSelectedAll: bulkActions.hasSelectedAll,
            });

        const shouldShowAcceptInvoiceMatchesButton =
            shouldShowAcceptSuggestedMatchesButton({
                selectedArray,
                hasSelectedAll: bulkActions.hasSelectedAll,
                hasSuggestedMatch: hasSuggestedInvoiceMatch,
            });

        const shouldShowAcceptBillMatchesButton =
            shouldShowAcceptSuggestedMatchesButton({
                selectedArray,
                hasSelectedAll: bulkActions.hasSelectedAll,
                hasSuggestedMatch: hasSuggestedBillMatch,
            });

        const selectedTransactionsWithSplit = selectedArray.filter(
            (value) => value.splitParentId,
        );

        const shouldShowUnsplitAllButton =
            selectedTransactionsWithSplit.length > 0 &&
            selectedTransactionsWithSplit.length === bulkActions.selectedCount;

        const areAnySelectedMatchedToInvoiceOrBill = selectedArray.some((t) =>
            isMatchedToInvoiceOrBill(t.transactionMatches, t.accrualMatches),
        );

        // Partitions the selection by the same invoice/bill match lock that
        // blocks class/counterparty edits on the single-row grid cells (see
        // MatchedTransactionFieldLockedError) — used to hide the Set
        // class/Set counterparty bulk actions entirely when every selected
        // transaction is locked, and to scope the update + heading to the
        // unlocked subset when the selection is mixed.
        const invoiceOrBillLockPartition =
            partitionSelectionByInvoiceOrBillLock({
                selectedArray,
                hasSelectedAll: bulkActions.hasSelectedAll,
            });
        const scheduleLockPartition = partitionSelectionByScheduleLock({
            selectedArray,
            hasSelectedAll: bulkActions.hasSelectedAll,
            ledgerId: primaryAccrualLedgerId,
        });
        const scheduleEntityLockPartition =
            partitionSelectionByScheduleEntityLock({
                selectedArray,
                hasSelectedAll: bulkActions.hasSelectedAll,
            });

        const shouldShowCopyLinkButton =
            bulkActions.selectedCount > 0 &&
            bulkActions.selectedCount <= TRANSACTIONS_PAGE_SIZE;

        const shouldShowCoaButton = uniqueEntityIds.length === 1;

        const shouldShowAccrualCoaButton =
            shouldShowCoaButton && hasAccrualLedger;

        const shouldShowSetScheduleButton = computeShouldShowSetScheduleButton({
            selectedCount: selectedArray.length,
            hasSelectedAll: bulkActions.hasSelectedAll,
        });

        const shouldShowRevertGLButton = selectedArray.some(
            (transaction) => transaction.accountId,
        );

        const shouldShowRemoveCounterpartyButton =
            invoiceOrBillLockPartition.unlockedTransactions.some(
                (transaction) => transaction.counterpartyId,
            );

        const hasInvoicedTransactionsInSelection =
            computeHasInvoicedTransactionsInSelection({
                selectedArray,
                hasSelectedAll: bulkActions.hasSelectedAll,
            });

        const shouldShowRemoveCustomerButton =
            computeShouldShowRemoveCustomerButton({
                selectedArray,
                hasSelectedAll: bulkActions.hasSelectedAll,
            });

        const shouldShowInvoiceToCustomerButton =
            !bulkActions.hasSelectedAll &&
            canInvoiceSelectedTransactionsToCustomer({
                selectedArray,
            });

        const shouldShowRemoveMemoButton = selectedArray.some(
            (transaction) =>
                transaction.memo != null && transaction.memo !== "",
        );

        const shouldShowRemoveClassesButton =
            invoiceOrBillLockPartition.unlockedTransactions.some(
                (transaction) =>
                    (transaction.classAssignments?.length ?? 0) > 0,
            );

        const shouldShowExclude1099Button = selectedArray.some(
            (t) => t.excludeFrom1099 !== true,
        );
        const shouldShowInclude1099Button =
            selectedArray.length > 0 &&
            selectedArray.every((t) => t.excludeFrom1099 === true);

        return {
            areClassesEnabled,
            areAnySelectedTransactionsLocked,
            shouldShowUnmatchAllButton,
            shouldShowMatchButton,
            shouldShowUnmatchInvoicesButton,
            shouldShowAcceptInvoiceMatchesButton,
            shouldShowAcceptBillMatchesButton,
            shouldShowUnsplitAllButton,
            areAnySelectedMatchedToInvoiceOrBill,
            invoiceOrBillLockPartition,
            scheduleLockPartition,
            scheduleEntityLockPartition,
            shouldShowCopyLinkButton,
            shouldShowCoaButton,
            shouldShowAccrualCoaButton,
            shouldShowSetScheduleButton,
            shouldShowRevertGLButton,
            shouldShowRemoveCounterpartyButton,
            shouldShowRemoveCustomerButton,
            shouldShowInvoiceToCustomerButton,
            shouldShowRemoveMemoButton,
            shouldShowRemoveClassesButton,
            shouldShowExclude1099Button,
            shouldShowInclude1099Button,
            hasInvoicedTransactionsInSelection,
        };
    }, [
        selectedArray,
        bulkActions.selectedCount,
        areClassesEnabled,
        uniqueEntityIds.length,
        hasAccrualLedger,
        primaryAccrualLedgerId,
        locks,
        bulkActions.hasSelectedAll,
    ]);
}
